import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ContactForm = () => {
  const { t, language } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success/error messages per language
  const messages = {
    de: {
      success: 'Ihre Nachricht wurde erfolgreich gesendet!',
      error: 'Es gab einen Fehler. Bitte versuchen Sie es später erneut.',
      connectionError: 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.'
    },
    en: {
      success: 'Your message has been sent successfully!',
      error: 'There was an error. Please try again later.',
      connectionError: 'Connection error. Please check your internet connection.'
    },
    th: {
      success: 'ส่งข้อความของคุณเรียบร้อยแล้ว!',
      error: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้งในภายหลัง',
      connectionError: 'เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต'
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    const lang = language || 'de';

    try {
      // Using Formspree for static website compatibility
      const response = await fetch('https://formspree.io/f/xreealql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Nicht angegeben',
          message: formData.message,
          _subject: `Neue Kontaktanfrage von ${formData.name}`
        }),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: messages[lang].success
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus({
          type: 'error',
          message: messages[lang].error
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: messages[lang].connectionError
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Phone label with optional text
  const phoneLabel = {
    de: 'Telefon (optional)',
    en: 'Phone (optional)',
    th: 'โทรศัพท์ (ไม่บังคับ)'
  };

  return (
    <Card className="p-6 md:p-8 shadow-xl border-0 bg-white rounded-2xl">
      <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">{t('contact.form.title')}</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.form.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
              placeholder={t('contact.form.namePlaceholder')}
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.form.emailLabel')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
              placeholder={t('contact.form.emailPlaceholder')}
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            {phoneLabel[language] || phoneLabel.en}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
            placeholder={t('contact.form.phonePlaceholder')}
          />
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            {t('contact.form.message')} *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors resize-none"
            placeholder={t('contact.form.messagePlaceholder')}
          />
        </div>

        {status.message && (
          <div className={`flex items-center gap-2 p-4 rounded-lg ${
            status.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {status.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{status.message}</span>
          </div>
        )}

        <div className="text-center pt-4">
          <Button 
            type="submit"
            disabled={isSubmitting}
            size="lg"
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('contact.form.sending')}
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {t('contact.form.send')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ContactForm;
